import { apiService } from './api';

/**
 * Frontend client for the Phase-2 QC controller.
 * The backend exposes /qc/charts (summaries), /qc/charts/detail (full series),
 * /qc/points (raw rows), and /qc/summary (per-client KPI block).
 */

export type QcControlType =
  | 'BLANK'
  | 'DUPLICATE_RPD'
  | 'CRM_RECOVERY'
  | 'PREP_CONCASSAGE_BLANK'
  | 'PREP_BROYAGE_BLANK'
  | 'PREP_CONCASSAGE_QUALITY'
  | 'PREP_BROYAGE_QUALITY';

/**
 * Synthetic identifiers used for prép-mécanique mass-loss blanks.
 * They live in the same `control_chart_points` table but are tagged with a
 * dedicated lab + element so they can be filtered out of the analytical
 * QC views and rendered in their own tab.
 */
export const PREP_MECANIQUE_LAB = 'PREP_MECANIQUE';
export const PREP_MASS_LOSS_ELEMENT = 'MASS_LOSS';
export const PREP_BLANK_CONTROL_TYPES: QcControlType[] = [
  'PREP_CONCASSAGE_BLANK',
  'PREP_BROYAGE_BLANK',
];

export interface QcChartSummary {
  chart_id: string;
  laboratory: string;
  element: string;
  control_type: QcControlType;
  reference_material?: string | null;
  total_points: number;
  in_control_percentage: number;
  current_mean: number;
  current_std_dev: number;
  baseline_mean: number;
  baseline_std_dev: number;
  ucl: number;
  lcl: number;
  uwl: number;
  lwl: number;
  last_violation_at?: string | null;
  /** CRM_RECOVERY only: certified reference value in its original unit (e.g. 0.322 for wt.% Cu). */
  certified_value?: number | null;
  certified_unit?: string | null;
}

export interface QcChartPoint {
  id: string;
  chart_id: string;
  laboratory: string;
  element: string;
  control_type: QcControlType;
  reference_material?: string | null;
  value: number;
  date: string;
  batch_id: string;
  sample_id?: string;
  mean: number;
  standard_deviation: number;
  ucl: number;
  lcl: number;
  uwl: number;
  lwl: number;
  out_of_control: boolean;
  warning_level: boolean;
  rule_violations?: Record<string, boolean>;
}

export interface QcChartDetailResponse {
  summary: QcChartSummary | null;
  points: QcChartPoint[];
}

export interface QcSummaryResponse {
  totals: {
    total_charts: number;
    total_points: number;
    degraded_charts: number;
    charts_with_recent_violation: number;
  };
  charts: QcChartSummary[];
}

export interface QcChartFilters {
  client_id?: string;
  laboratory?: string;
  element?: string;
  method?: string;
  control_type?: QcControlType;
  from?: string;
  to?: string;
}

export interface SampleQcStatus {
  role: string | null;
  status: 'IN_CONTROL' | 'WARNING' | 'OUT_OF_CONTROL' | 'NO_QC';
  sample_human_id: string | null;
  points: QcChartPoint[];
  last_evaluation_at: string | null;
  reasons: string[];
}

export interface BatchQcPreviewSample {
  sample_uuid: string;
  sample_human_id: string | null;
  role: string;
  blanc_subtype: string | null;
  parent_sample_human_id: string | null;
  reference_material_code: string | null;
  status: 'IN_CONTROL' | 'WARNING' | 'OUT_OF_CONTROL' | 'NO_QC' | 'PENDING';
  reasons: string[];
  points: QcChartPoint[];
  last_evaluation_at: string | null;
}

export interface BatchQcPreviewResponse {
  samples: BatchQcPreviewSample[];
  summary: {
    total: number;
    qc_samples: number;
    regular_samples: number;
    in_control: number;
    warning: number;
    out_of_control: number;
    pending: number;
    overall: 'OK' | 'WARNING' | 'OUT_OF_CONTROL' | 'NO_QC_TAGGED';
    violation_messages: string[];
  };
}

// ── Unified QA/QC dashboard (BGM-style, auto-populated) ──────────────────
export type QaQcSource = 'reception' | 'instrument';

export interface QaQcSeriesViolation {
  index: number;
  rule: string;
  message: string;
  date: string | null;
  value: number;
}

export interface QaQcCrmChart {
  source: QaQcSource;
  ref_label: string;
  element: string;
  laboratory: string;
  n: number;
  mean: number;
  sd: number;
  ucl: number;
  lcl: number;
  uwl: number;
  lwl: number;
  points: Array<{ date: string | null; batch_number: string | null; value: number; z: number }>;
  violations: QaQcSeriesViolation[];
}

export interface QaQcBlankChart {
  source: QaQcSource;
  blank_type: string;
  element: string;
  laboratory: string;
  n: number;
  ucl: number | null;
  points: Array<{ date: string | null; batch_number: string | null; value: number; censored: boolean }>;
}

export interface QaQcDuplicate {
  source: QaQcSource;
  date: string | null;
  batch_number: string | null;
  element: string;
  original: number | null;
  duplicate: number | null;
  rpd: number;
}

export interface QaQcRecoveryRow {
  source: QaQcSource;
  ref_label: string;
  element: string;
  n: number;
  observed_mean: number;
  observed_sd: number;
  cv_percent: number | null;
  violations_count: number;
}

export interface QaQcViolation {
  source: QaQcSource;
  ref_label: string;
  element: string;
  rule: string;
  date: string | null;
  batch_number: string | null;
  value: number;
  message: string;
}

export interface QaQcDashboardSummary {
  generated_at: string;
  filters: {
    client_id: string | null;
    laboratory: string | null;
    element: string | null;
    from: string | null;
    to: string | null;
    source: 'reception' | 'instrument' | 'both';
  };
  stats: {
    crm_series: number;
    blank_series: number;
    duplicate_points: number;
    recovery_rows: number;
    violations: number;
  };
  crm_charts: QaQcCrmChart[];
  blank_charts: QaQcBlankChart[];
  duplicates: QaQcDuplicate[];
  recovery: QaQcRecoveryRow[];
  violations: QaQcViolation[];
}

export interface QaQcDashboardFilters {
  client_id?: string;
  laboratory?: string;
  element?: string;
  source?: 'reception' | 'instrument' | 'both';
  from?: string;
  to?: string;
}

class QcService {
  private base = '/qc';

  async listCharts(f: QcChartFilters = {}): Promise<QcChartSummary[]> {
    const qs = this.qs(f);
    return apiService.get<QcChartSummary[]>(qs ? `${this.base}/charts?${qs}` : `${this.base}/charts`);
  }

  async getChartDetail(
    chartId: string,
    f: { from?: string; to?: string } = {},
  ): Promise<QcChartDetailResponse> {
    const params = new URLSearchParams();
    params.set('chart_id', chartId);
    if (f.from) params.set('from', f.from);
    if (f.to) params.set('to', f.to);
    return apiService.get<QcChartDetailResponse>(`${this.base}/charts/detail?${params.toString()}`);
  }

  async listPoints(f: QcChartFilters & { chart_id?: string } = {}): Promise<QcChartPoint[]> {
    const qs = this.qs(f);
    return apiService.get<QcChartPoint[]>(qs ? `${this.base}/points?${qs}` : `${this.base}/points`);
  }

  async getSummary(clientId?: string): Promise<QcSummaryResponse> {
    const url = clientId ? `${this.base}/summary?client_id=${encodeURIComponent(clientId)}` : `${this.base}/summary`;
    return apiService.get<QcSummaryResponse>(url);
  }

  /**
   * Unified QA/QC dashboard (BGM-style) — auto-populated from reception QC
   * (BIS/Blanc/MRC) + CSV/instrument QC (OREAS/blanks). Read-only.
   */
  async getDashboardSummary(
    f: QaQcDashboardFilters = {},
  ): Promise<QaQcDashboardSummary> {
    const qs = this.qs(f as Record<string, unknown>);
    return apiService.get<QaQcDashboardSummary>(
      qs ? `${this.base}/dashboard/summary?${qs}` : `${this.base}/dashboard/summary`,
    );
  }

  async recomputeChart(chartId: string): Promise<{ ok: true }> {
    return apiService.post<{ ok: true }>(
      `${this.base}/charts/${encodeURIComponent(chartId)}/recompute`,
    );
  }

  async recomputeSample(sampleUuid: string): Promise<{ ok: true; points: number }> {
    return apiService.post<{ ok: true; points: number }>(
      `${this.base}/samples/${sampleUuid}/recompute`,
    );
  }

  /**
   * Phase 2.4 — per-sample QC status. Used by the analyst/supervisor
   * dashboards to show a status chip (IN_CONTROL / WARNING / OUT_OF_CONTROL /
   * NO_QC) right after an analyst submits readings.
   */
  async getSampleStatus(sampleUuid: string): Promise<SampleQcStatus> {
    return apiService.get<SampleQcStatus>(`${this.base}/samples/${sampleUuid}/status`);
  }

  /**
   * Lot-level QC preview. Pass a list of sample UUIDs (typically the whole
   * batch the supervisor is reviewing) and optionally `recompute: true` to
   * force the QC engine to run for every QC sample BEFORE returning the
   * preview (used when lots predate the analyst-submit hook).
   */
  async getBatchPreview(
    sampleIds: string[],
    recompute = false,
  ): Promise<BatchQcPreviewResponse> {
    return apiService.post<BatchQcPreviewResponse>(`${this.base}/batch-preview`, {
      sampleIds,
      recompute,
    });
  }

  // -- client-portal aliases (use the /client-dashboard endpoints) --
  async listClientCharts(f: QcChartFilters = {}): Promise<QcChartSummary[]> {
    const qs = this.qs(f);
    return apiService.get<QcChartSummary[]>(
      qs ? `/client-dashboard/qc/charts?${qs}` : `/client-dashboard/qc/charts`,
    );
  }

  async getClientChartDetail(
    chartId: string,
    f: { from?: string; to?: string } = {},
  ): Promise<QcChartDetailResponse> {
    const params = new URLSearchParams();
    params.set('chart_id', chartId);
    if (f.from) params.set('from', f.from);
    if (f.to) params.set('to', f.to);
    return apiService.get<QcChartDetailResponse>(
      `/client-dashboard/qc/charts/detail?${params.toString()}`,
    );
  }

  private qs(f: Record<string, unknown> | object): string {
    const params = new URLSearchParams();
    Object.entries(f as Record<string, unknown>).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    return params.toString();
  }
}

export const qcService = new QcService();
export default qcService;
